import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import { isEmpty, isEqual } from "lodash";
import { createStructuredSelector } from "reselect";

import { actions } from "features/Model/Enclosure/_ducks";
import { useStateSelector } from "utils/field";
import { getValidation, requiredCodeRef, requiredIdOrCodeRef, getDecimalPlaces } from "utils/fieldValidation";
import { getOptions, getBaseUnits, getUnitOptions, getFoundationConfigParams } from "utils/fields";

import Field from "components/Field";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Tooltip from "components/Tooltip";
import Checkbox from "components/Input/Checkbox";

import Add from "assets/images/icons/JSX/Add";
import Book from "assets/images/icons/JSX/Book";
import Edit from "assets/images/icons/JSX/Edit";

import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";

const { setInitCode, updateCodeDrawer } = actions;

const slabTypeValidation = getValidation("crawlspaceSlabInsType");
const floorsAboveTypeValidation = getValidation("crawlspaceFloorsAboveType");

export const CodeFooter = ({ toggleDrawer, setInitCode, codeType }) => (
    <div
        className={classes.codeFooter}
        onClick={async () => {
            await setInitCode({}, codeType);
            toggleDrawer(true);
        }}
    >
        <span>Create New Code</span>
        <Add />
    </div>
);

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const LabelActionComponent = ({ codeRef, handleEdit, codeType }) => (
    <>
        <div
            className={globalStyles.editCode}
            data-tip="Edit Code"
            data-for={codeRef}
            onClick={(event) => handleEdit(event, codeRef, codeType)}
        >
            <Edit />
            <Tooltip id={codeRef} className={globalStyles.fieldTooltip} place="left" />
        </div>
    </>
);

// Slab Ins. Type codes found in code library
// Slabs don't have user defined codes
export const slabTypeLibCodesMap = (libCodes, handleEditLibSlabCode) =>
    Object.keys(libCodes)
        .map((codeRef) => {
            const { label = "" } = libCodes[codeRef] || {};
            const [standardCode] = codeRef.split("FloorsAdded-U");

            return {
                label: label,
                labelIcon: Book,
                labelActionComponent:
                    standardCode && handleEditLibSlabCode
                        ? () => <LabelActionComponent codeRef={codeRef} handleEdit={handleEditLibSlabCode} />
                        : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Slab Ins. Type codes found in model, filtered by those in library
// Slabs don't have user defined codes
export const slabTypeModelCodesMap = (modelCodes, libCodes, handleEditModelSlabCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "FloorsAdded") ||
                    !isEqual(modelCodes[codeRef].layers, libCodes[codeRef].layers)
                );
            }
            return !libCodes[codeRef] && component === "FloorsAdded";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("FloorsAdded-U");

            return {
                label,
                labelActionComponent: standardCode
                    ? () => <LabelActionComponent codeRef={codeRef} handleEdit={handleEditModelSlabCode} />
                    : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Floors Above Foundation Type codes found in code library
export const floorsAboveTypeLibCodesMap = (libFloorsAboveCodes, handleEditLibFloorsAboveCode) =>
    Object.keys(libFloorsAboveCodes)
        .map((codeRef) => {
            const { label = "" } = libFloorsAboveCodes[codeRef] || {};
            const [standardCode] = codeRef.split("FloorsAbove-U");

            return {
                label: label,
                labelIcon: Book,
                labelTag: !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditLibFloorsAboveCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditLibFloorsAboveCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Floors Above Foundation Type codes found in model, filtered by those in library
export const floorsAboveTypeModelCodesMap = (modelCodes, libCodes, handleEditModelFloorsAboveCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "FloorsAbove") ||
                    !isEqual(modelCodes[codeRef].layers, libCodes[codeRef].layers)
                );
            }
            return !libCodes[codeRef] && component === "FloorsAbove";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("FloorsAbove-U");

            return {
                label,
                labelTag: !standardCode ? "User Defined" : "",
                labelActionComponent: () => (
                    <LabelActionComponent
                        codeRef={codeRef}
                        handleEdit={handleEditModelFloorsAboveCode}
                        codeType={standardCode ? "S" : "U"}
                    />
                ),
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

const FloorConstruction = React.memo(
    ({
        accessor,
        componentAccessor = accessor,
        id,
        change,
        selectedUpgrade,
        canBeEditedFromParametric = true,
        disabledCodeEdit = false,
        isUpgrade = false,
        isCompact = false,
        setInitCode,
        updateCodeDrawer,
        isCommunity = false,
        isParametricAnalysis = false,
        codeRefs = [],
    }) => {
        const [isTouched, setIsTouched] = useState(false); // Hide warning when code is reselected but not yet added to ecmInputMap

        const {
            FloorsAdded: { codes: libSlabCodes = {} } = {},
            FloorsAbove: { codes: libFloorsAboveCodes = {} } = {},
        } = useStateSelector(`user.codeLib`) || {};
        const { calculatingRValue = {} } = useStateSelector(`enclosure`) || {};
        const { currentPackage = {} } = useStateSelector(`upgradeLibrary`) || {};
        const { parametricAnalysis = {} } = useStateSelector(`parametricAnalysis`) || {};
        const { community = {} } = useStateSelector(`community`) || {};
        const { baseModelId = "" } = useStateSelector(`commUpgrades`) || {};
        const { modelData = {} } = useStateSelector(`model`) || {};

        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};

        const allEcmInputMapUpgradesArr = ecmInputMap
            ? Object.keys(ecmInputMap)
                .map((key) =>
                    Object.entries(ecmInputMap[key])
                        .filter(([k, _]) => k !== "None")
                        .map(([k, v]) => ({ [k]: v }))
                )
                .flat()
            : [];

        const allEcmInputMapUpgrades = allEcmInputMapUpgradesArr.reduce((acc, curr) => ({ ...acc, ...curr }), {});

        const modelCodes =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.codes ||
            currentPackage?.upgrades?.[selectedUpgrade]?.codes ||
            modelData?.codes ||
            {};

        const modelUnits = modelData?.uiSettings?.primaryUnits || {};
        const { type: crawlspaceInsConfig } = useStateSelector(`${componentAccessor}.configuration`) || {};

        const {
            codeRef: selectedSlabCodeRef,
            id: slabPreBuiltId,
            value: slabPreBuiltValue,
        } = useStateSelector(`${accessor}.floor.slabInsType`) || {};
        const selectedSlabRValue = useStateSelector(`${accessor}.floor.slabInsType_nomRVal`) || 0;
        const slabCodeWarning = useStateSelector(`${accessor}.floor.slabInsType_warning`) || "";

        const { codeRef: selectedFloorsAboveCodeRef } = useStateSelector(`${accessor}.floor.floorsAboveInsType`) || {};
        const selectedFloorsAboveNomRValue = useStateSelector(`${accessor}.floor.floorsAboveInsType_nomRVal`) || 0;

        const floorsAboveCodeWarning = useStateSelector(`${accessor}.floor.floorsAboveInsType_warning`) || "";

        const rValUnitsSlab = useStateSelector(`${accessor}.floor.slabInsType_u`);
        const rValUnitsFloorsAbove = useStateSelector(`${accessor}.floor.floorsAboveInsType_u`);
        const {
            [id]: {
                floorsAboveInsType: isCalculatingRValue = false,
                slabInsType: isCalculatingSlabRValue = false,
            } = {},
        } = calculatingRValue;

        //Determine if selectedCodeRef is in modelCodes (attached properly)
        const modelCodesContainsSelected =
            Object.keys(modelCodes).includes(selectedFloorsAboveCodeRef) ||
            selectedFloorsAboveCodeRef === "UserSpecified" ||
            isTouched;

        const handleSlabTypeChange = useCallback(
            ({ codeRef = "", id = "", value = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    change(`${accessor}.floor.slabInsType_warning`, "");
                    return;
                }

                if (codeRef.includes("FloorsAdded")) {
                    const modelMatch = modelCodes[codeRef] || {};
                    if (!isEmpty(modelMatch)) {
                        change(`${accessor}.floor.slabInsType_nomRVal`, modelMatch.nominalRValue || 0);
                        change(`${accessor}.floor.slabInsType_effRVal`, modelMatch.nominalRValue || 0);
                        change(`${accessor}.floor.slabInsType_warning`, modelMatch.warningType || "");
                    } else {
                        const { nominalRValue = 0, warningType } = libSlabCodes[codeRef] || {};
                        change(`${accessor}.floor.slabInsType_nomRVal`, nominalRValue || 0);
                        change(`${accessor}.floor.slabInsType_effRVal`, nominalRValue || 0);
                        change(`${accessor}.floor.slabInsType_warning`, warningType || "");

                        if (isCommunity) {
                            change(`community.community.modelData.models.${baseModelId}.codes.${codeRef}`, { ...libSlabCodes[codeRef], codeRef });
                        } else {
                            change(`model.modelData.codes.${codeRef}`, { ...libSlabCodes[codeRef], codeRef });
                        }
                    }
                    //Redundancy
                    change(`model.stashedCodes.FloorsAdded.${accessor.split(".").slice(-1)[0]}`, codeRef);
                } else if (id != null) {
                    change(`${accessor}.floor.slabInsType_nomRVal`, value || 0);
                    change(`${accessor}.floor.slabInsType_effRVal`, value || 0);
                    change(`${accessor}.floor.slabInsType_warning`, "");
                }
            },
            [libSlabCodes, modelCodes, change, accessor, slabPreBuiltValue, isCommunity, baseModelId]
        );

        const handleFloorTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    change(`${accessor}.floor.floorsAboveInsType_warning`, "");
                    change(`${accessor}.floor.floorsAboveInsType_nomRVal`, selectedFloorsAboveNomRValue || 0.1);
                    change(`${accessor}.floor.floorsAboveInsType_effRVal`, selectedFloorsAboveNomRValue || 0.1);
                    return;
                }

                const modelMatch = modelCodes[codeRef] || {};
                if (!isEmpty(modelMatch)) {
                    change(`${accessor}.floor.floorsAboveInsType_nomRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.floor.floorsAboveInsType_effRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.floor.floorsAboveInsType_warning`, modelMatch.warningType || "");
                } else {
                    const { nominalRValue = 0, warningType = "" } = libFloorsAboveCodes[codeRef] || {};
                    change(`${accessor}.floor.floorsAboveInsType_nomRVal`, nominalRValue || 0.1);
                    change(`${accessor}.floor.floorsAboveInsType_effRVal`, nominalRValue || 0.1);
                    change(`${accessor}.floor.floorsAboveInsType_warning`, warningType || "");

                    if (isCommunity || isParametricAnalysis) {
                        setIsTouched(true);
                    } else {
                        change(`model.modelData.codes.${codeRef}`, { ...libFloorsAboveCodes[codeRef], codeRef });
                    }
                }
                //Redundancy
                change(`model.stashedCodes.FloorsAbove.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [libFloorsAboveCodes, modelCodes, change, accessor, isCommunity, isParametricAnalysis]
        );

        const handleEditModelSlabCode = useCallback(
            async (event, codeRef) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const code = modelCodes[codeRef];
                await setInitCode(code, "slabInsType");
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Slab (Floors Added)",
                    isEditing: true,
                    componentType: "FloorsAdded",
                    codeType: "S",
                    fieldAccessor: `${accessor}.slabInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibSlabCode = disabledCodeEdit
            ? null
            : useCallback(
                async (event, codeRef) => {
                    event.stopPropagation(); //Don't let click event bubble up to parent
                    const code = { ...libSlabCodes[codeRef], codeRef: codeRef, isLibCode: true };
                    await setInitCode(code, "slabInsType");
                    updateCodeDrawer({
                        isOpen: true,
                        codeName: "Slab (Floors Added)",
                        isEditing: true,
                        componentType: "FloorsAdded",
                        codeType: "S",
                        fieldAccessor: `${accessor}.slabInsType`,
                        componentId: id,
                        id: codeRef,
                        lastEdited: code.lastEdited || "",
                    });
                },
                [accessor, id, libSlabCodes, setInitCode, updateCodeDrawer]
            );

        const handleEditModelFloorsAboveCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); //Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "floorsAboveInsType";
                const code = modelCodes[codeRef];
                await setInitCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Floors Above",
                    isEditing: true,
                    componentType: "FloorsAbove",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.floor.floorsAboveInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibFloorsAboveCode = disabledCodeEdit
            ? null
            : useCallback(
                async (event, codeRef, codeType) => {
                    event.stopPropagation(); //Don't let click event bubble up to parent
                    const initCodeType = codeType === "U" ? "udefStandard" : "floorsAboveInsType";
                    const code = { ...libFloorsAboveCodes[codeRef], codeRef: codeRef, isLibCode: true };
                    await setInitCode(code, initCodeType);
                    updateCodeDrawer({
                        isOpen: true,
                        codeName: "Floors Above",
                        isEditing: true,
                        componentType: "FloorsAbove",
                        codeType: codeType,
                        fieldAccessor: `${accessor}.floor.floorsAboveInsType`,
                        componentId: id,
                        id: codeRef,
                        lastEdited: code.lastEdited || "",
                    });
                },
                [accessor, id, libFloorsAboveCodes, setInitCode, updateCodeDrawer]
            );

        const { slabIns: slabInsAllowed, slabCode: slabCodesAllowed } = getFoundationConfigParams({
            fieldKey: "crawlspaceInsConfig",
            configKey: crawlspaceInsConfig,
        });

        const lockSlabRVal = selectedSlabCodeRef == null;

        let slabDisplayRValue = 0;
        if (selectedSlabCodeRef !== "UserSpecified") {
            if (lockSlabRVal) {
                //Slab is locked to pre-build option
                // const {[slabPreBuiltId]: {value: {value: slabFixedValue = 0} = {}} = {}} = getOptions({fieldKey: 'crawlspaceSlabInsType'})

                slabDisplayRValue = slabPreBuiltValue;
            } else {
                //Allow code r-value
                slabDisplayRValue = selectedSlabRValue;
            }
        } else {
            slabDisplayRValue = selectedSlabRValue;
        }

        //Pre-define slab options, for the case that code selector options are not permitted
        let slabOpts = [
            {
                label: "User Specified",
                value: {
                    codeLabel: "User Specified",
                    codeRef: "UserSpecified",
                },
            },
        ];

        if (slabCodesAllowed) {
            slabOpts = [
                ...slabOpts,
                ...slabTypeModelCodesMap(modelCodes, libSlabCodes, handleEditModelSlabCode),
                ...slabTypeLibCodesMap(libSlabCodes, handleEditLibSlabCode),
            ];
        }

        slabOpts = [...slabOpts, ...getOptions({ fieldKey: "crawlspaceSlabInsType" })];

        const gridTemplate = isCompact ? "1fr 1fr 1fr 1fr 1fr" : isUpgrade ? "2fr 1fr 2fr" : "31.85% 23.25% 15% 15%";

        return (
            <>
                <InputRow gridTemplate={gridTemplate} compact={isCompact} lastRow>
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.floor.slabInsType`}
                        options={slabOpts.map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value?.codeRef),
                        }))}
                        label="Slab Insulation Type"
                        placeholder="Choose Slab Insulation Type"
                        searchPlaceholder="Search Slab Insulation Codes"
                        search={slabCodesAllowed}
                        footer={() =>
                            slabCodesAllowed ? (
                                <CodeFooter
                                    toggleDrawer={(value) =>
                                        updateCodeDrawer({
                                            isOpen: value,
                                            codeName: "Slab (Floors Added)",
                                            isEditing: false,
                                            componentType: "FloorsAdded",
                                            codeType: "S",
                                            fieldAccessor: `${accessor}.slabInsType`,
                                            componentId: id,
                                        })
                                    }
                                    setInitCode={setInitCode}
                                    codeType="slabInsType"
                                />
                            ) : (
                                <></>
                            )
                        }
                        className={classes.slabType}
                        disabled={!slabInsAllowed || !canBeEditedFromParametric}
                        isLoading={isCalculatingSlabRValue}
                        onChange={handleSlabTypeChange}
                        validate={slabInsAllowed ? requiredIdOrCodeRef : undefined}
                        compact={isCompact}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={
                            lockSlabRVal
                                ? `${accessor}.floor.slabInsType.value`
                                : `${accessor}.floor.slabInsType_nomRVal`
                        }
                        label={
                            selectedSlabCodeRef === "UserSpecified" || lockSlabRVal
                                ? "Effective R-Value"
                                : "Nominal R-Value"
                        }
                        placeholder={0.0}
                        disabled={
                            selectedSlabCodeRef !== "UserSpecified" ||
                            lockSlabRVal ||
                            !slabInsAllowed ||
                            !canBeEditedFromParametric
                        }
                        validate={slabTypeValidation}
                        setTouched={true}
                        decimals={getDecimalPlaces("crawlspaceSlabInsType")}
                        change={change}
                        setValue={slabDisplayRValue}
                        units={{
                            base: getBaseUnits("crawlspaceSlabInsType", modelUnits),
                            options: getUnitOptions("thermalResistance"),
                            selected: rValUnitsSlab,
                            unitType: "thermalResistance",
                            accessor: `${accessor}.floor.slabInsType_u`,
                        }}
                        onChange={(value) => {
                            if (selectedSlabCodeRef === "UserSpecified") {
                                change(`${accessor}.floor.slabInsType_nomRVal`, value);
                                change(`${accessor}.floor.slabInsType_effRVal`, value);
                            }
                        }}
                        info={
                            "Values calculated by the Code Selector are nominal R-values. HOT2000 re-calculates R-values based on the content of the code when running a simulation."
                        }
                        isLoading={isCalculatingSlabRValue}
                        warning={slabCodeWarning}
                        compact={isCompact}
                    />
                    {isCompact ? (
                        <div>
                            <Field
                                component={Checkbox}
                                name={`${accessor}.floor.isBelowFrostline`}
                                label="Is Below Frostline?"
                                large
                                className={classes.floorCheckbox}
                                type="checkbox"
                                disabled={!canBeEditedFromParametric}
                                compact={isCompact}
                            />
                            <Field
                                component={Checkbox}
                                name={`${accessor}.floor.heatedFloor`}
                                label="Is Floor Heated?"
                                large
                                className={classes.floorCheckbox}
                                type="checkbox"
                                disabled={!canBeEditedFromParametric}
                                compact={isCompact}
                            />
                        </div>
                    ) : (
                        <>
                            <Field
                                component={Checkbox}
                                name={`${accessor}.floor.isBelowFrostline`}
                                label="Is Below Frostline?"
                                large
                                className={classes.floorCheckbox}
                                type="checkbox"
                                disabled={!canBeEditedFromParametric}
                                compact={isCompact}
                            />
                            <Field
                                component={Checkbox}
                                name={`${accessor}.floor.heatedFloor`}
                                label="Is Floor Heated?"
                                large
                                className={classes.floorCheckbox}
                                type="checkbox"
                                disabled={!canBeEditedFromParametric}
                                compact={isCompact}
                            />
                        </>
                    )}
                    <Field
                        component={Select}
                        name={`${accessor}.floor.floorsAboveInsType`}
                        options={[
                            {
                                label: "User Specified",
                                value: {
                                    codeLabel: "User Specified",
                                    codeRef: "UserSpecified",
                                },
                            },
                            ...floorsAboveTypeModelCodesMap(
                                modelCodes,
                                libFloorsAboveCodes,
                                handleEditModelFloorsAboveCode
                            ),
                            ...floorsAboveTypeLibCodesMap(libFloorsAboveCodes, handleEditLibFloorsAboveCode),
                        ].map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value?.codeRef),
                        }))}
                        label="Floors Above Foundation Type"
                        placeholder="Choose Type"
                        searchPlaceholder="Search Codes"
                        search={true}
                        validate={requiredCodeRef}
                        footer={() => (
                            <CodeFooter
                                toggleDrawer={(value) =>
                                    updateCodeDrawer({
                                        isOpen: value,
                                        codeName: "Floors Above",
                                        isEditing: false,
                                        componentType: "FloorsAbove",
                                        codeType: "S",
                                        fieldAccessor: `${accessor}.floor.floorsAboveInsType`,
                                        componentId: id,
                                    })
                                }
                                setInitCode={setInitCode}
                                codeType="floorsAboveInsType"
                            />
                        )}
                        className={classes.floorsAboveType}
                        isLoading={isCalculatingRValue}
                        onChange={handleFloorTypeChange}
                        disabled={!canBeEditedFromParametric}
                        compact={isCompact}
                        warning={!modelCodesContainsSelected ? "codeDataMissing" : null}
                    />
                    {selectedFloorsAboveCodeRef === "UserSpecified" ? (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.floor.floorsAboveInsType_effRVal`}
                            label={"Effective R-Value"}
                            placeholder={0.0}
                            disabled={selectedFloorsAboveCodeRef !== "UserSpecified" || !canBeEditedFromParametric}
                            validate={floorsAboveTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("crawlspaceFloorsAboveType")}
                            change={change}
                            units={{
                                base: getBaseUnits("crawlspaceFloorsAboveType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnitsFloorsAbove,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.floor.floorsAboveInsType_u`,
                            }}
                            onChange={(value) => {
                                if (selectedFloorsAboveCodeRef === "UserSpecified") {
                                    change(`${accessor}.floor.floorsAboveInsType_nomRVal`, value || 0.1);
                                }
                            }}
                            info={"User specified values are assumed to be effective R-values."}
                            // warning={selectedFloorsAboveNomRValue === 0 ? 'floorsAboveZeroRVal' : ''}
                            compact={isCompact}
                        />
                    ) : (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.floor.floorsAboveInsType_nomRVal`}
                            label={"Nominal R-Value"}
                            placeholder={0.0}
                            disabled={true}
                            validate={floorsAboveTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("crawlspaceFloorsAboveType")}
                            change={change}
                            setValue={selectedFloorsAboveNomRValue || 0.1}
                            units={{
                                base: getBaseUnits("crawlspaceFloorsAboveType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnitsFloorsAbove,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.floor.floorsAboveInsType_u`,
                            }}
                            info={
                                "Values calculated by the Code Selector are nominal R-values. HOT2000 re-calculates R-values based on the content of the code when running a simulation."
                            }
                            warning={floorsAboveCodeWarning}
                            isLoading={isCalculatingRValue}
                            compact={isCompact}
                        />
                    )}
                </InputRow>
            </>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    setInitCode: async (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
});

export default connect(mapStateToProps, mapDispatchToProps)(FloorConstruction);
